import { ENVIRONMENT_INITIALIZER, EnvironmentProviders, ErrorHandler, Provider, inject } from "@angular/core";
import { registerCrashlyticsReporter } from "app/core/crashlytics/crashlytics-reporter";
import { CrashlyticsService } from "app/core/crashlytics/crashlytics.service";
import { GlobalErrorHandler } from "app/core/crashlytics/global-error-handler";
import { reportInterruptedCapture } from "app/core/crashlytics/native-capture";

/**
 * Verification hooks, reachable from a WebView devtools console
 * (chrome://inspect) on a debug build:
 *
 *   pmsCrashlytics.nonFatal()   -> non-fatal report, app keeps running
 *   pmsCrashlytics.jsError()    -> uncaught Angular error via GlobalErrorHandler
 *   pmsCrashlytics.crash()      -> native fatal crash, app closes
 *
 * Nothing here runs on its own; each has to be invoked deliberately.
 * See docs/firebase-crashlytics.md for the full verification procedure.
 */
const installTestHooks = (crashlytics: CrashlyticsService): void => {
  (window as any).pmsCrashlytics = {
    nonFatal: () => crashlytics.recordException(new Error("PMS test non-fatal"), "Manual verification"),
    jsError: () => {
      throw new Error("PMS test uncaught JavaScript error");
    },
    crash: () => crashlytics.crash("PMS test native crash"),
  };
};

export const provideCrashlytics = (): Array<Provider | EnvironmentProviders> => {
  return [
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler,
    },
    {
      provide: ENVIRONMENT_INITIALIZER,
      useValue: () => {
        const crashlytics = inject(CrashlyticsService);

        // Make the singleton reachable from helpers that run outside the injection context (see crashlytics-reporter.ts).
        registerCrashlyticsReporter(crashlytics);

        // The interrupted-capture check must run after init(), which is what enables collection and makes the plugin usable.
        void crashlytics.init().then(() => reportInterruptedCapture(crashlytics));

        installTestHooks(crashlytics);
      },
      multi: true,
    },
  ];
};
